import React, { useState, useEffect, useMemo, useRef } from 'react';
import styles from '../../styles/MateriasStyles/PantallaDeTareas.v2.module.css';
import Pagination from '../../components/Pagination.tsx';
import { usePermissions } from '../../hooks/usePermissions';

const MateriasTable = ({ query = '', materias = [], onView, onEdit, onDelete, loading = false, isSearching = false }) => {
  // Los datos ya vienen filtrados desde el backend usando `search`.
  const filtered = materias || [];

  // PERMISOS
  const { canEdit, canDelete } = usePermissions();
  const puedeEditar = canEdit('Gestión Escolar');
  const puedeEliminar = canDelete('Gestión Escolar');

  // PAGINATION LOGIC
  const [page, setPage] = useState(1);
  const pageSize = 8;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Crear clave única basada en IDs para detectar cambios reales
  const materiasKey = useMemo(() => {
    return (materias || []).map(m => m.id_materia || m.id).sort().join(',');
  }, [materias]);

  const prevMateriasKeyRef = useRef(materiasKey);

  // Reiniciar página solo cuando realmente cambia la lista de materias
  useEffect(() => {
    if (prevMateriasKeyRef.current !== materiasKey) {
      setPage(1);
      prevMateriasKeyRef.current = materiasKey;
    }
  }, [materiasKey]);

  // Ajustar página si quedó fuera de rango
  useEffect(() => {
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const ActionButtons = ({ materia }) => (
    <div className={styles.actionButtons}>
      <button aria-label="Ver detalles" className={styles.actionButton} onClick={() => onView && onView(materia)}>
        <img
          src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/a1782ac5b190a9d29d8f798383eedda3f294d56b?placeholderIfAbsent=true"
          alt="Ver"
          className={styles.actionIcon}
        />
      </button>
      {puedeEditar && (
        <button aria-label="Editar" className={styles.actionButton} onClick={() => onEdit && onEdit(materia)}>
          <img
            src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/83eacec9f1f974b33615bbc95aea7ff7b2d13b0e?placeholderIfAbsent=true"
            alt="Editar"
            className={styles.actionIcon}
          />
        </button> 
      )}
      {puedeEliminar && (
        <button aria-label="Eliminar" className={styles.actionButton} onClick={() => onDelete && onDelete(materia)}>
          <img
            src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/bef20ed4bd5293db48e37df8e511dca0fed7db2e?placeholderIfAbsent=true"
            alt="Eliminar"
            className={styles.actionIcon}
          />
        </button>
      )}
      {!puedeEditar && !puedeEliminar && (
        <span style={{ color: '#888', fontSize: '12px' }}>Solo lectura</span>
      )}
    </div>
  );


  if (loading) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <div className={styles.tableTitle}>
            <div className={styles.tableTitleIcon}>
              <div className={styles.iconContainerOrange}>
                <span>📕</span>
              </div>
            </div>
            <h2 className={styles.tableTitleText}>Materias</h2>
          </div>
        </div>
        <div className={styles.tableHeaderRow}>
          <div className={styles.columnHeader}><span>Materia</span></div>
          <div className={styles.columnHeader}><span>Sección</span></div>
          <span className={styles.columnHeaderWide}>Academia</span>
          <span className={styles.columnHeader}>Clave</span>
          <div className={styles.columnHeader}><span>Estado</span></div>
          <span className={styles.columnHeaderWide + ' ' + styles.stickyActionsHeader}>Acciones</span>
        </div>
        <div style={{ padding: '20px', textAlign: 'center' }}>Cargando materias...</div>
      </div>
    );
  }

  if (!paginated.length) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <div className={styles.tableTitle}>
            <div className={styles.tableTitleIcon}>
              <div className={styles.iconContainerOrange}>
                <span>📕</span>
              </div>
            </div>
            <h2 className={styles.tableTitleText}>Materias</h2>
          </div>
        </div>
        <div className={styles.tableHeaderRow}>
          <div className={styles.columnHeader}><span>Materia</span></div>
          <div className={styles.columnHeader}><span>Sección</span></div>
          <span className={styles.columnHeaderWide}>Academia</span>
          <span className={styles.columnHeader}>Clave</span>
          <div className={styles.columnHeader}><span>Estado</span></div>
          <span className={styles.columnHeaderWide + ' ' + styles.stickyActionsHeader}>Acciones</span>
        </div>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          {isSearching
            ? `Sin resultados para “${query}”.`
            : 'No hay materias registradas.'}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer} style={{ position: 'relative', minHeight: '320px', zIndex: 1 }}>
      <div className={styles.tableHeader}>
        <div className={styles.tableTitle}>
          <div className={styles.tableTitleIcon}>
            <div className={styles.iconContainerOrange}>
              <span>📕</span>
            </div>
          </div>
          <h2 className={styles.tableTitleText}>Materias</h2>
        </div>
      </div>

      <div className={styles.tableHeaderRow}>
        <div className={styles.columnHeader}>
          <span>Materia</span>
          <img
            src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/ac3418739771e5c9f651d8b5020bd45222ab4f63?placeholderIfAbsent=true"
            alt="Sort"
            className={styles.sortIcon}
          />
        </div>
        <div className={styles.columnHeader}>
          <span>Sección</span>
          <img
            src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/ac3418739771e5c9f651d8b5020bd45222ab4f63?placeholderIfAbsent=true"
            alt="Sort"
            className={styles.sortIcon}
          />
        </div>
        <span className={styles.columnHeaderWide}>Academia</span>
        <span className={styles.columnHeader}>Clave</span>
        <div className={styles.columnHeader}>
          <span>Estado</span>
          <img
            src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/ac3418739771e5c9f651d8b5020bd45222ab4f63?placeholderIfAbsent=true"
            alt="Sort"
            className={styles.sortIcon}
          />
        </div>
        <span className={styles.columnHeaderWide + ' ' + styles.stickyActionsHeader}>Acciones</span>
      </div>

      <div className={styles.tableBody}>
        {paginated.length === 0 && (
          <div className={styles.tableRow}>
            <div className={styles.programInfo}>
              <span className={styles.programName}>{query ? `Sin resultados para "${query}".` : 'Sin resultados.'}</span>
            </div>
            <span className={styles.cellData}>—</span>
            <span className={styles.cellData}>—</span>
            <span className={styles.cellData}>—</span>
            <span className={styles.cellData}>—</span>
            <div className={styles.statusCell}>
              <div className={styles.statusInactive}><span>—</span></div>
            </div>
            <div className={styles.actionButtons + ' ' + styles.stickyActionsCell} />
          </div>
        )}
        {paginated.map((materia, index) => (
          <div key={materia.id_materia || index} className={styles.tableRow}>
            <div className={styles.programInfo}>
              <span className={styles.programName}>{materia.nombre || '—'}</span>
            </div>
            <span className={styles.cellData}>{materia.seccion_nombre || materia.section || '—'}</span>
            <span className={styles.cellData}>{materia.academia_nombre || materia.academy || '—'}</span>
            <span className={styles.cellData}>{materia.clave || materia.code || '—'}</span>
            <div className={styles.statusCell}>
              <div className={materia.estado === 'ACTIVO' || materia.isActive ? styles.statusActive : styles.statusInactive}>
                <span>{materia.estado || materia.status || 'INACTIVO'}</span>
              </div>
            </div>
            <div className={styles.actionButtons + ' ' + styles.stickyActionsCell}>
              <ActionButtons materia={materia} />
            </div>
          </div>
        ))}
      </div>

      {/* Paginación global reutilizada de Programas Académicos */}
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </div>
  );
};

export default React.memo(MateriasTable);
